/**
 * Agent Management Service
 *
 * This service handles the communication between the admin UI and agents.
 * It provides functions to fetch, update, and monitor agents.
 *
 * IMPORTANT: All field names MUST match agent.js configuration fields exactly.
 */

import { toast } from 'react-toastify';

// Real-time communication functions (placeholders)
export const sendCommandToAgent = async (agentId, commandData) => {
  console.log(`[MQTT] Would send command to agent ${agentId}:`, commandData);
  return Promise.resolve({ success: true });
};

export const sendConfigToAgent = async (agentId, configData) => {
  console.log(`[MQTT] Would send config to agent ${agentId}:`, configData);
  return Promise.resolve({ success: true });
};

export const subscribeToAgentStatus = (agentId, callback) => {
  console.log(`[MQTT] Would subscribe to agent ${agentId} status`);
  return () => console.log(`[MQTT] Would unsubscribe from agent ${agentId} status`);
};

// Add missing subscribeToAgentResults function
export const subscribeToAgentResults = (agentId, callback) => {
  console.log(`[MQTT] Would subscribe to agent ${agentId} results`);
  return () => console.log(`[MQTT] Would unsubscribe from agent ${agentId} results`);
};

export const listenForAllAgents = callback => {
  console.log(`[MQTT] Would listen for all agents`);
  return () => console.log(`[MQTT] Would stop listening for all agents`);
};

// Local cache of known agents and their status
const agentCache = new Map();

/**
 * Fetch all known agents
 * Returns data from the agent cache
 */
export const fetchAgents = async () => {
  try {
    // Initialize agent monitoring if not already done
    // This ensures we're listening for agent updates even if called separately
    if (agentCache.size === 0) {
      console.log('Agent cache is empty, initializing monitoring...');
      const cleanup = initAgentMonitoring();
      // Don't actually call cleanup - we want monitoring to continue
    }

    // Return the cached agents if any exist
    if (agentCache.size > 0) {
      return Array.from(agentCache.values());
    }

    // If no agents in cache, return empty array
    console.log('No agents found in cache, returning empty array');
    return [];
  } catch (error) {
    console.error('Error fetching agents:', error);
    throw error;
  }
};

/**
 * Update agent configuration
 * This sends a config update to the agent and updates local cache
 *
 * @param {Object} agent - The updated agent configuration
 */
export const updateAgent = async agent => {
  try {
    // Send configuration update to agent via MQTT
    // This would trigger the agent to update its .env file
    await sendConfigToAgent(agent.agentId, {
      // Map UI fields to agent configuration fields
      // IMPORTANT: These MUST match agent.js expectations exactly
      agentName: agent.agentName,
      agentTenant: agent.agentTenant,
      agentSipId: agent.agentSipId,
      agentRestId: agent.agentRestId,
      heartbeatInterval: agent.heartbeatInterval,
      features: agent.features,
    });

    // Update local cache
    agentCache.set(agent.agentId, agent);

    // TODO: In production, also update backend/database

    return agent;
  } catch (error) {
    console.error('Error updating agent:', error);
    throw error;
  }
};

/**
 * Create a new agent
 * In a real implementation, this would create the agent in your backend
 * and potentially trigger agent deployment/enrollment
 *
 * @param {Object} agent - The new agent configuration
 */
export const createAgent = async agent => {
  try {
    // For now, just add to local cache
    agentCache.set(agent.agentId, {
      ...agent,
      status: 'pending', // Agent hasn't connected yet
    });

    // TODO: In production, create in backend/database
    // and handle agent enrollment

    return agent;
  } catch (error) {
    console.error('Error creating agent:', error);
    throw error;
  }
};

/**
 * Enable or disable an agent
 * Sends a command to the agent to enable/disable itself
 *
 * @param {string} agentId - The agent ID
 * @param {boolean} enabled - Whether to enable or disable
 */
export const toggleAgentStatus = async (agentId, enabled) => {
  try {
    // Send command to agent
    await sendCommandToAgent(agentId, {
      type: enabled ? 'enable' : 'disable',
      timestamp: new Date().toISOString(),
    });

    // Update local cache if it exists
    if (agentCache.has(agentId)) {
      const agent = agentCache.get(agentId);
      agentCache.set(agentId, {
        ...agent,
        enabled,
      });
    }

    return { agentId, enabled };
  } catch (error) {
    console.error('Error toggling agent status:', error);
    throw error;
  }
};

/**
 * Restart an agent
 * Sends a restart command to the agent
 *
 * @param {string} agentId - The agent ID
 */
export const restartAgent = async agentId => {
  try {
    // Send restart command to agent
    await sendCommandToAgent(agentId, {
      type: 'restart',
      timestamp: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error restarting agent:', error);
    throw error;
  }
};

/**
 * Initialize agent monitoring
 * This sets up listeners for agent status updates and populates the agent cache
 */
export const initAgentMonitoring = () => {
  console.log('Initializing agent monitoring with MQTT...');

  // Track heartbeat counts to reduce logging frequency
  const heartbeatCounts = {};

  // Handle agent status updates
  const handleAgentStatus = (agentId, statusData) => {
    // Only log status updates occasionally to reduce noise
    if (!heartbeatCounts[agentId]) {
      heartbeatCounts[agentId] = 0;
      console.log(`Received first status from agent ${agentId}:`, statusData);
    } else {
      heartbeatCounts[agentId]++;

      // Log only every 10th heartbeat
      if (heartbeatCounts[agentId] % 10 === 0) {
        console.log(`Received heartbeat #${heartbeatCounts[agentId]} from ${agentId}`);
      }
    }

    // Update agent in cache
    if (agentCache.has(agentId)) {
      const agent = agentCache.get(agentId);
      const updatedAgent = {
        ...agent,
        status: statusData.status || 'unknown',
        lastSeen: new Date().toISOString(),
      };

      // IMPORTANT: Always extract fields from the status message
      // This ensures the UI always shows the most recent agent name and other fields
      updatedAgent.agentName = statusData.agentName || updatedAgent.agentName;
      updatedAgent.features = statusData.features || updatedAgent.features;
      updatedAgent.agentTenant = statusData.agentTenant || updatedAgent.agentTenant;
      updatedAgent.agentSipId = statusData.agentSipId || updatedAgent.agentSipId;
      updatedAgent.agentRestId = statusData.agentRestId || updatedAgent.agentRestId;
      updatedAgent.heartbeatInterval =
        statusData.heartbeatInterval || updatedAgent.heartbeatInterval;

      agentCache.set(agentId, updatedAgent);

      // Only log significant changes, not every update
      if (heartbeatCounts[agentId] === 1 || heartbeatCounts[agentId] % 30 === 0) {
        console.log(`Updated agent in cache: ${agentId}`, updatedAgent);
      }
    } else {
      // New agent discovered via MQTT
      const newAgent = {
        agentId,
        agentName: statusData.agentName || `Agent ${agentId}`,
        agentTenant: statusData.agentTenant || 'unknown',
        agentSipId: statusData.agentSipId || 'na',
        agentRestId: statusData.agentRestId || 'na',
        heartbeatInterval: statusData.heartbeatInterval || 30000,
        status: statusData.status || 'unknown',
        features: statusData.features || [],
        lastSeen: new Date().toISOString(),
        _source: 'MQTT',
      };

      agentCache.set(agentId, newAgent);
      console.log(`New agent added to cache: ${agentId}`, newAgent);

      // Notify user of new agent
      toast.info(`New agent discovered: ${statusData.agentName || agentId}`);
    }
  };

  // Listen for all agents
  return listenForAllAgents(handleAgentStatus);
};

// Export API
export default {
  fetchAgents,
  updateAgent,
  createAgent,
  toggleAgentStatus,
  restartAgent,
  initAgentMonitoring,
};
